import { FieldType } from "../../type-system/FieldType";
import {
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanKey,
  ShopifySellingPlanToSellingPlanGroupKey,
  ShopifySellingPlanToShopKey,
  ShopifyShopKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToSellingPlanKey,
} from "./keys";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import { ShopifyApiVersion } from "./ShopifyApiVersion";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySellingPlanKey,
  "2022-01": {
    name: "Shopify Selling Plan",
    fields: [
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        ...on SellingPlanRecurringBillingPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          interval
          intervalCount
          maxCycles
          minCycles
        }`,
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `
        ...on SellingPlanRecurringDeliveryPolicy {
          anchors {
            day
            month
            type
          }
          createdAt
          cutoff
          intent
          interval
          intervalCount
          preAnchorBehavior
        }
        `,
          },
        },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "name" } },
      },
      {
        name: "Options",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "options" } },
      },
      {
        name: "Position",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "position" } },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Pricing Policies",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "pricingPolicies",
            graphQLSelection: `
        ...on SellingPlanFixedPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          createdAt
        }
        ...on SellingPlanRecurringPricingPolicy {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue {
              percentage
            }
          }
          afterCycle
          createdAt
        }`,
          },
        },
      },
      {
        name: "Selling Plan Group",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanToSellingPlanGroupKey,
        configuration: {
          relatedModelKey: ShopifySellingPlanGroupKey,
        },
        // this field is not in the Shopify GraphQL API but we do sync it with our payloader processor
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sellingPlanGroupId", skipGraphQLSelection: true } },
      },
      {
        name: "Subscription Lines",
        fieldType: FieldType.HasMany,
        configuration: {
          relatedModelKey: ShopifySubscriptionLineKey,
          inverseFieldKey: ShopifySubscriptionLineToSellingPlanKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySellingPlanToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "sellingPlanGroup.sellingPlans",
      parent: "sellingPlanGroups",
    },
  },
  "2022-07": {
    fields: [
      {
        name: "Category",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "category" } },
        configuration: {
          allowOther: true,
          options: [{ name: "OTHER" }, { name: "PRE_ORDER" }, { name: "SUBSCRIPTION" }, { name: "TRY_BEFORE_YOU_BUY" }],
        },
      },
      {
        name: "Inventory Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "inventoryPolicy", graphQLSelection: "reserve" } },
      },
    ],
  },
  "2024-07": {
    hasMetafields: true,
  },
};

export const ShopifySellingPlan = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
